/**
 * Каталог обучения (E4.6a): восемь уроков, демонстрация и три самостоятельных задания из
 * замороженного макета (docs/design/frontend-v2/training-data.js), 43 шага суммарно. Тексты --
 * в локали learning, здесь порядок, аудитория и адреса. Адрес шага строится из manifest
 * учебного экземпляра: ни один шаг не ссылается на рабочий объект.
 */

import type { TrainingManifest } from '@/api/training';
import type { WorkspaceIconName } from '@/components/workspace/WorkspaceIcon';
import { RECEIVERS_TAB } from '@/pages/Integrations/Targets/paths';
import { resourcePath } from '@/components/workspace/resourcePath';
import { EVENTS_PATH, eventPath } from '@/pages/Events/paths';
import { LESSON_STEPS } from './lessonSteps';

export const LESSON_IDS = [
	'quick',
	'map',
	'investigation',
	'source',
	'integrations',
	'daily',
	'policy',
	'ai',
	'demo',
] as const;
export type LessonId = (typeof LESSON_IDS)[number];

export const EXERCISE_IDS = ['daily', 'policy', 'ai'] as const;
export type ExerciseId = (typeof EXERCISE_IDS)[number];

export type Audience = 'all' | 'analyst' | 'admin';

export const STEP_IDS = [
	'overview',
	'priority',
	'resource',
	'grouping',
	'clusters',
	'filters',
	'node',
	'access',
	'event',
	'receivers',
	'profile',
	'deliveries',
	'gaps',
	'signals',
	'sensors',
	'inventory',
	'reports',
	'daily-priority',
	'daily-assignment',
	'daily-followup',
	'policy-scope',
	'policy-condition',
	'policy-response',
	'policy-event',
	'ai-routes',
	'ai-masking',
	'ai-blocking',
	'ai-events',
] as const;
export type StepId = (typeof STEP_IDS)[number];

export interface Lesson {
	id: LessonId;
	icon: WorkspaceIconName;
	minutes: number;
	audience: Audience;
	exercise?: ExerciseId;
	steps: readonly StepId[];
}

export const LESSONS: Readonly<Record<LessonId, Lesson>> = {
	quick: {
		id: 'quick',
		icon: 'overview',
		minutes: 3,
		audience: 'all',
		steps: LESSON_STEPS.quick,
	},
	map: {
		id: 'map',
		icon: 'map',
		minutes: 4,
		audience: 'analyst',
		steps: LESSON_STEPS.map,
	},
	investigation: {
		id: 'investigation',
		icon: 'shield',
		minutes: 5,
		audience: 'analyst',
		steps: LESSON_STEPS.investigation,
	},
	source: {
		id: 'source',
		icon: 'sensor',
		minutes: 4,
		audience: 'admin',
		steps: LESSON_STEPS.source,
	},
	integrations: {
		id: 'integrations',
		icon: 'plug',
		minutes: 5,
		audience: 'admin',
		steps: LESSON_STEPS.integrations,
	},
	daily: {
		id: 'daily',
		icon: 'calendar',
		minutes: 4,
		audience: 'analyst',
		exercise: 'daily',
		steps: LESSON_STEPS.daily,
	},
	policy: {
		id: 'policy',
		icon: 'shield',
		minutes: 4,
		audience: 'analyst',
		exercise: 'policy',
		steps: LESSON_STEPS.policy,
	},
	ai: {
		id: 'ai',
		icon: 'ai',
		minutes: 4,
		audience: 'all',
		exercise: 'ai',
		steps: LESSON_STEPS.ai,
	},
	demo: {
		id: 'demo',
		icon: 'path',
		minutes: 7,
		audience: 'all',
		steps: LESSON_STEPS.demo,
	},
};

/** Самостоятельные задания: цели проверяются по порядку, ID целей хранит прогресс */
export interface Exercise {
	id: ExerciseId;
	lesson: LessonId;
	icon: WorkspaceIconName;
	minutes: number;
	goals: readonly string[];
}

export const EXERCISES: Readonly<Record<ExerciseId, Exercise>> = {
	daily: {
		id: 'daily',
		lesson: 'daily',
		icon: 'calendar',
		minutes: 3,
		goals: ['resource', 'event', 'assignment'],
	},
	policy: { id: 'policy', lesson: 'policy', icon: 'shield', minutes: 3, goals: ['saved'] },
	ai: { id: 'ai', lesson: 'ai', icon: 'ai', minutes: 2, goals: ['masked', 'blocked'] },
};

/** Уроки каталога без демонстрации: она показывается отдельным блоком */
export const CATALOG_LESSONS: readonly LessonId[] = LESSON_IDS.filter((id) => id !== 'demo');

/** Самостоятельная практика «Первые шаги»: три действия после знакомства */
export const PRACTICE_IDS = ['resource', 'map', 'event'] as const;
export type PracticeId = (typeof PRACTICE_IDS)[number];

/** Учебные объекты, на которые ссылаются шаги: ключи сценария platform-v1 */
const EXPORTS = 'exports';
const PRIMARY_EVENT = 'EV-1042';
const PUBLIC_DATA_POLICY = 'public-data';
const SIEM = 'siem';
const SPLUNK = 'email-soc';
const MONQ = 'external-api';
const MOSCOW = 'moscow';
const PROD_CLUSTER = 'prod-cluster';

/** Элемент карты хранилища выгрузок: вид узла карты и учебный ID */
const EXPORTS_MAP_TYPE = 's3_object_store';

function query(path: string, params: Record<string, string | string[]>): string {
	const search = new URLSearchParams();
	Object.entries(params).forEach(([name, value]) =>
		(Array.isArray(value) ? value : [value]).forEach((item) => search.append(name, item)),
	);
	return `${path}?${search.toString()}`;
}

/** В manifest учебного экземпляра нет объекта, на который ссылается шаг */
export class MissingManifestEntry extends Error {
	readonly kind: string;
	readonly key: string;

	constructor(kind: string, key: string) {
		super([kind, key].join(':'));
		this.name = 'MissingManifestEntry';
		this.kind = kind;
		this.key = key;
	}
}

function present<T>(value: T | undefined, kind: string, key: string): T {
	if (value === undefined) throw new MissingManifestEntry(kind, key);
	return value;
}

function exportsRef(manifest: TrainingManifest) {
	return present(manifest.resources[EXPORTS], 'resource', EXPORTS);
}

function eventId(manifest: TrainingManifest): string {
	return present(manifest.events[PRIMARY_EVENT], 'event', PRIMARY_EVENT);
}

function policyPath(manifest: TrainingManifest): string {
	const policy = present(manifest.policies[PUBLIC_DATA_POLICY], 'policy', PUBLIC_DATA_POLICY);
	return `/policies/${encodeURIComponent(policy)}`;
}

function recipient(manifest: TrainingManifest, key: string): string {
	return encodeURIComponent(present(manifest.recipients[key], 'recipient', key));
}

/** Адрес шага в учебном экземпляре; параметр экземпляра добавляет workspaceHref */
const STEP_ROUTES: Readonly<Record<StepId, (manifest: TrainingManifest) => string>> = {
	overview: () => '/overview',
	priority: () => '/overview',
	resource: (m) => resourcePath(exportsRef(m)),
	grouping: () => query('/data-map', { grouping: 'region-cluster' }),
	clusters: () => query('/data-map', { grouping: 'cluster' }),
	filters: (m) =>
		query('/data-map', {
			grouping: 'region-cluster',
			region: present(m.regions[MOSCOW], 'region', MOSCOW),
			cluster: present(m.clusters[PROD_CLUSTER], 'cluster', PROD_CLUSTER),
		}),
	node: (m) => query('/data-map', { node: `${EXPORTS_MAP_TYPE}/${exportsRef(m).id}` }),
	access: (m) => `${resourcePath(exportsRef(m))}/access`,
	event: (m) => eventPath(eventId(m)),
	receivers: () => query('/integrations', { tab: RECEIVERS_TAB }),
	profile: (m) => `/integrations/targets/${recipient(m, SIEM)}`,
	deliveries: (m) => query(`/integrations/targets/${recipient(m, SPLUNK)}`, { tab: 'deliveries' }),
	gaps: (m) => query(`/integrations/targets/${recipient(m, SIEM)}`, { tab: 'gaps' }),
	signals: (m) => query(`/integrations/targets/${recipient(m, MONQ)}`, { tab: 'signals' }),
	sensors: () => '/infrastructure-access/all',
	inventory: () => '/data-storages',
	reports: () => '/reports',
	'daily-priority': () => EVENTS_PATH,
	'daily-assignment': (m) => eventPath(eventId(m)),
	'daily-followup': () => EVENTS_PATH,
	'policy-scope': policyPath,
	'policy-condition': policyPath,
	'policy-response': policyPath,
	'policy-event': (m) => eventPath(eventId(m)),
	'ai-routes': () => '/llm-gate/routes',
	'ai-masking': () => '/llm-gate/policies',
	'ai-blocking': () => '/llm-gate/policies',
	'ai-events': () => '/llm-gate/events',
};

/** Адрес шага: только объекты учебного manifest */
export function stepRoute(step: StepId, manifest: TrainingManifest): string {
	if (manifest.schema_version !== 1)
		throw new MissingManifestEntry('schema', String(manifest.schema_version));
	return STEP_ROUTES[step](manifest);
}

/** Страница, с которой начинается самостоятельное задание (макет: entry) */
const EXERCISE_ENTRY: Readonly<Record<ExerciseId, string>> = {
	daily: '/data-map',
	policy: '/policies',
	ai: '/llm-gate',
};

export function exerciseRoute(exercise: ExerciseId): string {
	return EXERCISE_ENTRY[exercise];
}

/** Адрес практики «Первых шагов» */
export function practiceRoute(practice: PracticeId, manifest: TrainingManifest): string {
	return stepRoute(practice === 'map' ? 'node' : practice, manifest);
}

/** Все шаги каталога по порядку уроков: 43 */
export function allSteps(): { lesson: LessonId; index: number; step: StepId }[] {
	return LESSON_IDS.flatMap((lesson) =>
		LESSONS[lesson].steps.map((step, index) => ({ lesson, index, step })),
	);
}

/** Адрес совпадает с адресом шага: тот же путь и те же значения его параметров */
export function matchesRoute(pathname: string, search: string, route: string): boolean {
	const [path, query = ''] = route.split('?');
	if (pathname !== path) return false;
	const actual = new URLSearchParams(search);
	return [...new URLSearchParams(query).entries()].every(([name, value]) =>
		actual.getAll(name).includes(value),
	);
}

/** Практика «Первых шагов», которую выполняет открытый адрес учебного экземпляра */
export function practiceAt(
	pathname: string,
	search: string,
	manifest: TrainingManifest,
): PracticeId | null {
	return (
		PRACTICE_IDS.find((practice) =>
			matchesRoute(pathname, search, practiceRoute(practice, manifest)),
		) ?? null
	);
}
